import {
  ComponentPropsWithRef,
  ComponentType,
  createElement,
  useState,
} from 'react';

import { lazyWithRetry } from './lazyWithRetry';

export type PreloadableComponent<T extends ComponentType<any>> = T & {
  preload: () => Promise<T>;
};

export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  const ReactLazyComponent = lazyWithRetry(factory);
  let PreloadedComponent: T | undefined;
  let factoryPromise: Promise<T> | undefined;

  const Component = function LazyWithPreload(props: ComponentPropsWithRef<T>) {
    // Once one of these is chosen, we must ensure that it continues to be
    // used for all subsequent renders, otherwise it can cause the
    // underlying component to be unmounted and remounted.
    const [{ value: ComponentToRender }] = useState({
      value: PreloadedComponent ?? ReactLazyComponent,
    });
    return createElement(ComponentToRender, props as any);
  };

  const LazyWithPreload = Component as any as PreloadableComponent<T>;

  LazyWithPreload.preload = () => {
    if (!factoryPromise) {
      factoryPromise = factory().then((module) => {
        PreloadedComponent = module.default;
        return PreloadedComponent;
      });
    }

    return factoryPromise;
  };

  return LazyWithPreload;
}
